import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import Footer from './Footer';

// Simple icon components
const Shield = ({ className = "" }) => (
  <svg className={`w-6 h-6 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const Lock = ({ className = "" }) => (
  <svg className={`w-6 h-6 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const Eye = ({ className = "" }) => (
  <svg className={`w-6 h-6 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const Users = ({ className = "" }) => (
  <svg className={`w-6 h-6 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
  </svg>
);

const Mail = ({ className = "" }) => (
  <svg className={`w-6 h-6 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const ArrowLeft = ({ className = "" }) => (
  <svg className={`w-4 h-4 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-white relative overflow-hidden">
      {/* Interactive AI Background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,100,255,0.05),transparent)] animate-pulse"></div>
        <div className="absolute top-10 left-10 w-32 h-32 border border-cyan-400/20 rounded-lg rotate-45 animate-spin-slow opacity-30"></div>
        <div className="absolute top-32 right-20 w-24 h-24 border border-purple-400/20 rounded-full animate-bounce opacity-40"></div>
      </div>

      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-cyan-400/20 sticky top-0 z-50 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center shadow-lg shadow-cyan-400/30">
                <span className="text-white font-bold text-xl">AI</span>
              </div>
              <span className="text-white text-xl font-bold">AI Agency</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 relative z-10">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center text-cyan-400 hover:text-cyan-300 transition-colors duration-200 mb-8"
        >
          <ArrowLeft className="mr-2" />
          {t('nav.backToHome')}
        </button>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full mb-6 shadow-lg shadow-cyan-400/30">
            <Shield className="text-white w-10 h-10" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500 bg-clip-text text-transparent">
              Πολιτική Απορρήτου
            </span>
          </h1>
          <p className="text-cyan-400 text-sm">Τελευταία ενημέρωση: 03.03.2026</p>
        </div>

        <Card className="bg-white/5 backdrop-blur-sm border border-white/10">
          <CardContent className="p-8 md:p-12 text-gray-300 space-y-8">
            
            <div className="prose prose-invert max-w-none">
              <p className="text-lg leading-relaxed mb-8">
                Η παρούσα Πολιτική Απορρήτου περιγράφει τον τρόπο με τον οποίο η PROTOS AI AGENCY συλλέγει, χρησιμοποιεί, αποθηκεύει και προστατεύει τα προσωπικά δεδομένα των επισκεπτών και χρηστών της ιστοσελίδας ai-agency.gr. Η επεξεργασία των δεδομένων πραγματοποιείται σύμφωνα με τον Γενικό Κανονισμό Προστασίας Δεδομένων (ΕΕ) 2016/679 (GDPR) και την ισχύουσα ελληνική νομοθεσία.
              </p>

              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-bold text-cyan-400 mb-4">1. Υπεύθυνος Επεξεργασίας</h2>
                  <p className="leading-relaxed mb-4">Υπεύθυνος επεξεργασίας των προσωπικών σας δεδομένων είναι:</p>
                  <div className="bg-white/5 rounded-lg p-6 border border-cyan-500/20">
                    <p className="font-semibold text-white mb-2">PROTOS AI AGENCY</p>
                    <p>Διεύθυνση: Arxeologiikou Mousiou 17, 54640, Thessaloniki, Greece</p>
                    <p>Email: info@ai-agency.gr</p>
                    <p>Τηλέφωνο: +30 210 440 7770</p>
                    <p>ΑΦΜ: 802208607</p>
                    <p>ΓΕΜΗ: 172146906000</p>
                  </div>
                  <p className="mt-4 text-sm text-gray-400">Εάν κάποια από τα ανωτέρω στοιχεία αλλάξει ή δεν είναι πλήρως ακριβή, η παρούσα πολιτική πρέπει να επικαιροποιηθεί άμεσα.</p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-cyan-400 mb-4">2. Ποια δεδομένα συλλέγουμε</h2>
                  <p className="leading-relaxed mb-4">Κατά την επίσκεψη και χρήση της ιστοσελίδας μας ενδέχεται να συλλέγουμε τις ακόλουθες κατηγορίες δεδομένων:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Στοιχεία ταυτοποίησης και επικοινωνίας (όνομα, επωνυμία, email, τηλέφωνο)</li>
                    <li>Στοιχεία που μας αποστέλλετε μέσω φόρμας επικοινωνίας ή email</li>
                    <li>Τεχνικά δεδομένα (IP διεύθυνση, τύπος συσκευής, πρόγραμμα περιήγησης, λειτουργικό σύστημα)</li>
                    <li>Δεδομένα χρήσης της ιστοσελίδας</li>
                    <li>Δεδομένα που σχετίζονται με cookies και παρόμοιες τεχνολογίες</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-cyan-400 mb-4">3. Σκοποί επεξεργασίας</h2>
                  <p className="leading-relaxed mb-4">Επεξεργαζόμαστε τα προσωπικά σας δεδομένα για τους εξής σκοπούς:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Για την απάντηση σε αιτήματα επικοινωνίας</li>
                    <li>Για την παροχή πληροφοριών σχετικά με τις υπηρεσίες μας</li>
                    <li>Για τη διαχείριση επιχειρηματικών επικοινωνιών και πιθανών συνεργασιών</li>
                    <li>Για τη βελτίωση της λειτουργίας, της ασφάλειας και της απόδοσης της ιστοσελίδας</li>
                    <li>Για στατιστική ανάλυση χρήσης, όπου αυτό επιτρέπεται</li>
                    <li>Για συμμόρφωση με νομικές υποχρεώσεις</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-cyan-400 mb-4">4. Νομική βάση επεξεργασίας</h2>
                  <p className="leading-relaxed mb-4">Η επεξεργασία των προσωπικών σας δεδομένων βασίζεται, κατά περίπτωση, στις ακόλουθες νομικές βάσεις:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Άρθρο 6 παρ. 1 στοιχ. α GDPR – συγκατάθεση</li>
                    <li>Άρθρο 6 παρ. 1 στοιχ. β GDPR – εκτέλεση σύμβασης ή λήψη μέτρων πριν από σύμβαση</li>
                    <li>Άρθρο 6 παρ. 1 στοιχ. γ GDPR – συμμόρφωση με νομική υποχρέωση</li>
                    <li>Άρθρο 6 παρ. 1 στοιχ. στ GDPR – έννομο συμφέρον για ασφαλή και αποτελεσματική λειτουργία της ιστοσελίδας και της επιχειρηματικής μας δραστηριότητας</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-cyan-400 mb-4">5. Φόρμες επικοινωνίας και άμεση επικοινωνία</h2>
                  <p className="leading-relaxed">
                    Εάν επικοινωνήσετε μαζί μας μέσω φόρμας, email ή τηλεφώνου, επεξεργαζόμαστε τα δεδομένα που μας παρέχετε αποκλειστικά για την εξέταση του αιτήματός σας, την επικοινωνία μαζί σας και την προετοιμασία ενδεχόμενης συνεργασίας ή παροχής υπηρεσιών. Τα δεδομένα αυτά δεν χρησιμοποιούνται για άσχετους σκοπούς χωρίς τη συγκατάθεσή σας.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-cyan-400 mb-4">6. Cookies και παρόμοιες τεχνολογίες</h2>
                  <p className="leading-relaxed mb-4">
                    Η ιστοσελίδα μας χρησιμοποιεί cookies. Ορισμένα cookies είναι τεχνικά αναγκαία για τη σωστή λειτουργία της ιστοσελίδας, ενώ άλλα χρησιμοποιούνται μόνο μετά από τη συγκατάθεσή σας, π.χ. για στατιστικούς ή/και διαφημιστικούς σκοπούς.
                  </p>
                  <p className="leading-relaxed mb-4">Μπορείτε να διαχειριστείτε τις προτιμήσεις σας μέσω των Ρυθμίσεων Cookies της ιστοσελίδας.</p>
                  <div className="bg-white/5 rounded-lg p-6 border border-cyan-500/20">
                    <p className="font-semibold text-white mb-3">Κατηγορίες cookies:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li><strong>Αναγκαία cookies:</strong> απαιτούνται για βασικές λειτουργίες της ιστοσελίδας</li>
                      <li><strong>Στατιστικά cookies:</strong> βοηθούν στην κατανόηση της χρήσης της ιστοσελίδας</li>
                      <li><strong>Λειτουργικά cookies:</strong> αποθηκεύουν προτιμήσεις χρήστη</li>
                      <li><strong>Marketing cookies:</strong> χρησιμοποιούνται μόνο εφόσον δοθεί συγκατάθεση</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-cyan-400 mb-4">7. Αρχεία καταγραφής διακομιστή (Server Logs)</h2>
                  <p className="leading-relaxed mb-4">Για λόγους ασφάλειας, σταθερότητας και τεχνικής υποστήριξης, ο διακομιστής μας μπορεί να καταγράφει αυτόματα πληροφορίες όπως:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>IP διεύθυνση</li>
                    <li>ημερομηνία και ώρα πρόσβασης</li>
                    <li>σελίδες που επισκεφθήκατε</li>
                    <li>referrer URL</li>
                    <li>τύπο browser και λειτουργικού συστήματος</li>
                  </ul>
                  <p className="mt-4 leading-relaxed">Η επεξεργασία αυτή βασίζεται στο έννομο συμφέρον μας για την ασφάλεια και την ακεραιότητα της ιστοσελίδας.</p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-cyan-400 mb-4">8. Αποδέκτες δεδομένων</h2>
                  <p className="leading-relaxed mb-4">Τα προσωπικά σας δεδομένα ενδέχεται να διαβιβάζονται μόνο όπου είναι απαραίτητο σε:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>παρόχους φιλοξενίας (hosting providers)</li>
                    <li>τεχνικούς συνεργάτες και διαχειριστές υποδομών</li>
                    <li>παρόχους εργαλείων επικοινωνίας, analytics ή cloud</li>
                    <li>νομικούς, λογιστικούς ή κανονιστικούς συμβούλους</li>
                    <li>δημόσιες αρχές, όπου αυτό απαιτείται από τον νόμο</li>
                  </ul>
                  <p className="mt-4 leading-relaxed font-semibold text-white">Δεν πωλούμε τα προσωπικά σας δεδομένα σε τρίτους.</p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-cyan-400 mb-4">9. Διαβίβαση δεδομένων εκτός ΕΕ/ΕΟΧ</h2>
                  <p className="leading-relaxed mb-4">
                    Εφόσον χρησιμοποιούνται πάροχοι που εδρεύουν ή επεξεργάζονται δεδομένα εκτός Ευρωπαϊκής Ένωσης / ΕΟΧ (π.χ. ΗΠΑ), η διαβίβαση πραγματοποιείται μόνο με κατάλληλες εγγυήσεις, όπως:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Απόφαση επάρκειας της Ευρωπαϊκής Επιτροπής, ή/και</li>
                    <li>Τυποποιημένες Συμβατικές Ρήτρες (SCCs), ή/και</li>
                    <li>άλλα νόμιμα μέτρα συμμόρφωσης</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-cyan-400 mb-4">10. Χρόνος διατήρησης δεδομένων</h2>
                  <p className="leading-relaxed mb-4">
                    Διατηρούμε τα προσωπικά δεδομένα μόνο για όσο χρονικό διάστημα απαιτείται για τον σκοπό για τον οποίο συλλέχθηκαν ή για όσο επιβάλλεται από τη νομοθεσία.
                  </p>
                  <div className="bg-white/5 rounded-lg p-6 border border-cyan-500/20">
                    <p className="font-semibold text-white mb-3">Ενδεικτικά:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>αιτήματα επικοινωνίας: έως 12–24 μήνες</li>
                      <li>εμπορική / προσυμβατική επικοινωνία: έως την ολοκλήρωση της αξιολόγησης</li>
                      <li>λογιστικά / φορολογικά στοιχεία: σύμφωνα με τις νόμιμες υποχρεώσεις</li>
                      <li>δεδομένα που βασίζονται σε συγκατάθεση: έως την ανάκλησή της</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-cyan-400 mb-4">11. Ασφάλεια δεδομένων</h2>
                  <p className="leading-relaxed mb-4">Λαμβάνουμε κατάλληλα τεχνικά και οργανωτικά μέτρα για την προστασία των προσωπικών δεδομένων από:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>μη εξουσιοδοτημένη πρόσβαση</li>
                    <li>απώλεια</li>
                    <li>αλλοίωση</li>
                    <li>καταστροφή</li>
                    <li>μη εξουσιοδοτημένη κοινολόγηση</li>
                  </ul>
                  <p className="mt-4 text-sm text-gray-400">
                    Παρά τα μέτρα που λαμβάνουμε, καμία μετάδοση μέσω διαδικτύου ή ηλεκτρονικό σύστημα αποθήκευσης δεν μπορεί να θεωρηθεί απολύτως ασφαλές.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-cyan-400 mb-4">12. Τα δικαιώματά σας</h2>
                  <p className="leading-relaxed mb-4">Σύμφωνα με τον GDPR, έχετε τα ακόλουθα δικαιώματα:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Δικαίωμα πρόσβασης</li>
                    <li>Δικαίωμα διόρθωσης</li>
                    <li>Δικαίωμα διαγραφής</li>
                    <li>Δικαίωμα περιορισμού της επεξεργασίας</li>
                    <li>Δικαίωμα φορητότητας</li>
                    <li>Δικαίωμα εναντίωσης</li>
                    <li>Δικαίωμα ανάκλησης συγκατάθεσης οποτεδήποτε</li>
                    <li>Δικαίωμα υποβολής καταγγελίας στην αρμόδια εποπτική αρχή</li>
                  </ul>
                  <p className="mt-4 leading-relaxed">
                    Στην Ελλάδα, αρμόδια εποπτική αρχή είναι η Αρχή Προστασίας Δεδομένων Προσωπικού Χαρακτήρα (ΑΠΔΠΧ).
                  </p>
                  <div className="mt-6 bg-cyan-500/10 rounded-lg p-6 border border-cyan-500/30">
                    <p className="text-white font-semibold mb-2">Για την άσκηση των δικαιωμάτων σας:</p>
                    <p>Email: <a href="mailto:info@ai-agency.gr" className="text-cyan-400 hover:text-cyan-300">info@ai-agency.gr</a></p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-cyan-400 mb-4">13. Σύνδεσμοι προς τρίτες ιστοσελίδες</h2>
                  <p className="leading-relaxed">
                    Η ιστοσελίδα μας ενδέχεται να περιέχει συνδέσμους προς ιστότοπους τρίτων. Δεν φέρουμε ευθύνη για το περιεχόμενο, τις πρακτικές απορρήτου ή την ασφάλεια αυτών των ιστοτόπων. Σας συνιστούμε να διαβάζετε τις αντίστοιχες πολιτικές απορρήτου τους.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-cyan-400 mb-4">14. Αλλαγές στην παρούσα πολιτική</h2>
                  <p className="leading-relaxed">
                    Διατηρούμε το δικαίωμα να τροποποιούμε την παρούσα Πολιτική Απορρήτου οποτεδήποτε, ώστε να αντικατοπτρίζει αλλαγές στη νομοθεσία, στις υπηρεσίες μας ή στον τρόπο λειτουργίας της ιστοσελίδας μας. Η εκάστοτε ισχύουσα έκδοση θα δημοσιεύεται στην παρούσα σελίδα.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-cyan-400 mb-4">15. Επικοινωνία</h2>
                  <p className="leading-relaxed mb-4">
                    Για οποιοδήποτε ζήτημα σχετικά με την παρούσα Πολιτική Απορρήτου ή την επεξεργασία των προσωπικών σας δεδομένων, μπορείτε να επικοινωνείτε μαζί μας:
                  </p>
                  <div className="bg-gradient-to-r from-cyan-500/10 to-blue-600/10 rounded-lg p-6 border border-cyan-500/30">
                    <p className="mb-2"><strong className="text-white">Email:</strong> <a href="mailto:info@ai-agency.gr" className="text-cyan-400 hover:text-cyan-300">info@ai-agency.gr</a></p>
                    <p><strong className="text-white">Τηλέφωνο:</strong> +30 210 440 7770</p>
                  </div>
                </section>
              </div>
            </div>

          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
